/**
 * In-place patcher for EST .xlsx / .xlsm workbooks.
 *
 * Why this exists:
 *   SheetJS Community Edition (xlsx@0.18) does NOT preserve charts, images,
 *   conditional formatting, pivot tables, data validations, themes, drawings,
 *   or many other "design" features when you read+write a workbook. The only
 *   way to keep the user's EST file byte-identical (graphs, fonts, colors,
 *   columns, formulas, macros) is to leave the original .xlsx ZIP package
 *   alone and surgically rewrite ONLY the cells we own (G..J) inside the
 *   target worksheet's sheetN.xml.
 *
 * This module does exactly that, using JSZip (already a project dependency).
 */

import JSZip from "jszip";

export type EstCellPatch = {
  /** 1-indexed Excel row number (e.g. 5 means row 5). */
  row: number;
  /**
   * Excel column letter (single A..Z). For EST exports we currently use
   * G/H/I/J for Price/52WH/52WL/Spread% and W for the daily change %.
   */
  col: "G" | "H" | "I" | "J" | "W";
  /** Numeric value to write. NaN/Infinity/non-numbers are skipped by the caller. */
  value: number;
  /**
   * Optional Excel number format code (e.g. "0.00"). When omitted, the cell's
   * existing style index (s="...") is preserved so the EST template's column
   * formatting (currency, decimals, percent) continues to apply unchanged.
   */
  numFmt?: string;
};

/**
 * Text patch for an arbitrary worksheet (used for the PRVFIN tab where we write
 * the exchange/index name into column C of rows 336–356). Stored as an inline
 * string so we don't need to mutate the workbook's shared strings table.
 */
export type EstTextPatch = {
  row: number;
  col: string; // single letter A..Z
  value: string;
};

export type PatchResult = {
  bytes: Uint8Array;
  /** Name of the sheet inside the workbook that was patched. */
  sheetName: string;
  /** Internal ZIP path of the worksheet XML that was patched. */
  sheetPath: string;
  /** Number of cells actually written. */
  cellsWritten: number;
};

const SHEET_ALIASES = ["realtimedata", "realtime"];
const normKey = (s: string) =>
  s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");


/** Tiny attribute extractor — robust enough for the well-formed XML Excel emits. */
const attr = (xml: string, name: string): string | null => {
  const m = xml.match(new RegExp(`\\s${name}="([^"]*)"`));
  return m ? m[1] : null;
};

/** Extract every <sheet .../> entry from xl/workbook.xml. */
const parseSheets = (workbookXml: string): { name: string; sheetId: string; rId: string }[] => {
  const out: { name: string; sheetId: string; rId: string }[] = [];
  // Match <sheet ... /> — note we can't use [^/] because Target attrs (in rels)
  // and other XML can contain slashes. We instead match minimally up to "/>".
  const re = /<sheet\b[^>]*?\/>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(workbookXml)) !== null) {
    const tag = m[0];
    const name = attr(tag, "name") ?? "";
    const sheetId = attr(tag, "sheetId") ?? "";
    const rId = attr(tag, "r:id") ?? attr(tag, "id") ?? "";
    if (name && rId) out.push({ name, sheetId, rId });
  }
  return out;
};

/** Build rId -> Target map from xl/_rels/workbook.xml.rels. */
const parseRels = (relsXml: string): Map<string, string> => {
  const map = new Map<string, string>();
  const re = /<Relationship\b[^>]*?\/>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(relsXml)) !== null) {
    const tag = m[0];
    const id = attr(tag, "Id");
    const target = attr(tag, "Target");
    if (id && target) map.set(id, target);
  }
  return map;
};

/** Convert a 1-indexed column letter to its 0-based index. Supports A..Z. */
const colLetterToIndex = (letter: string): number => letter.charCodeAt(0) - 65;

/** Internal generic patch (numeric OR inline string) used by patchRow. */
type GenericPatch =
  | { col: string; kind: "number"; value: number; styleOverride?: string }
  | { col: string; kind: "inlineStr"; value: string; styleOverride?: string };

/** XML-escape a string for safe inclusion as text content. */
const xmlEscape = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Patch a single <row> element's XML by inserting/replacing the <c> for the
 * given column(s). Returns the new row XML.
 *
 *   - Numeric patch  → `<c r="G5" s="..."><v>123.45</v></c>`
 *   - Inline string  → `<c r="C336" s="..." t="inlineStr"><is><t>NYSE</t></is></c>`
 *
 * Existing style indices (`s="..."`) are preserved so the template's column
 * formatting (currency, decimals, fonts, fills) keeps applying.
 */
const patchRow = (
  rowXml: string,
  rowNumber: number,
  patches: GenericPatch[],
): string => {
  const openMatch = rowXml.match(/^<row\b[^>]*>/);
  if (!openMatch) return rowXml;
  const openTag = openMatch[0];
  const inner = rowXml.slice(openTag.length, rowXml.lastIndexOf("</row>"));

  type Cell = { ref: string; colIdx: number; xml: string; styleAttr: string };
  const cells: Cell[] = [];
  const cellRe = /<c\b[^>]*(?:\/>|>[\s\S]*?<\/c>)/g;
  let m: RegExpExecArray | null;
  while ((m = cellRe.exec(inner)) !== null) {
    const cellXml = m[0];
    const ref = attr(cellXml, "r") ?? "";
    const colMatch = ref.match(/^([A-Z]+)/);
    if (!colMatch) continue;
    const colIdx = colLetterToIndex(colMatch[1]);
    const styleMatch = cellXml.match(/\ss="(\d+)"/);
    cells.push({ ref, colIdx, xml: cellXml, styleAttr: styleMatch ? ` s="${styleMatch[1]}"` : "" });
  }

  for (const patch of patches) {
    const ref = `${patch.col}${rowNumber}`;
    const targetIdx = colLetterToIndex(patch.col);
    const existing = cells.find((c) => c.ref === ref);
    const styleAttr = patch.styleOverride
      ? ` s="${patch.styleOverride}"`
      : existing?.styleAttr ?? "";
    const newCellXml =
      patch.kind === "number"
        ? `<c r="${ref}"${styleAttr}><v>${patch.value}</v></c>`
        : `<c r="${ref}"${styleAttr} t="inlineStr"><is><t xml:space="preserve">${xmlEscape(patch.value)}</t></is></c>`;
    if (existing) {
      existing.xml = newCellXml;
    } else {
      cells.push({ ref, colIdx: targetIdx, xml: newCellXml, styleAttr });
    }
  }

  cells.sort((a, b) => a.colIdx - b.colIdx);

  let newOpenTag = openTag;
  const maxCol = cells.reduce((m, c) => Math.max(m, c.colIdx + 1), 0);
  if (maxCol > 0) {
    if (/\sspans="/.test(newOpenTag)) {
      newOpenTag = newOpenTag.replace(/\sspans="[^"]*"/, ` spans="1:${maxCol}"`);
    } else {
      newOpenTag = newOpenTag.replace(/^<row/, `<row spans="1:${maxCol}"`);
    }
  }

  return `${newOpenTag}${cells.map((c) => c.xml).join("")}</row>`;
};


/**
 * Apply all `patches` to the given worksheet XML. Patches are grouped per row
 * and inserted into existing <row> elements, or new <row> elements are added
 * (in row-number order) when a target row doesn't yet exist in <sheetData>.
 */
const patchSheetXml = (sheetXml: string, patches: GenericPatch[] & { row: number }[] | Array<GenericPatch & { row: number }>): string => {
  if (patches.length === 0) return sheetXml;

  // Group by row number.
  const byRow = new Map<number, Array<GenericPatch & { row: number }>>();
  for (const p of patches as Array<GenericPatch & { row: number }>) {
    const arr = byRow.get(p.row) ?? [];
    arr.push(p);
    byRow.set(p.row, arr);
  }

  // Find <sheetData> ... </sheetData>
  const openIdx = sheetXml.indexOf("<sheetData>");
  const selfClosed = sheetXml.match(/<sheetData\s*\/>/);
  let sdOpen: number;
  let sdClose: number;
  let sheetDataInner: string;

  if (openIdx !== -1) {
    sdOpen = openIdx + "<sheetData>".length;
    sdClose = sheetXml.indexOf("</sheetData>", sdOpen);
    sheetDataInner = sheetXml.slice(sdOpen, sdClose);
  } else if (selfClosed) {
    const idx = sheetXml.indexOf(selfClosed[0]);
    sheetXml = sheetXml.slice(0, idx) + "<sheetData></sheetData>" + sheetXml.slice(idx + selfClosed[0].length);
    sdOpen = sheetXml.indexOf("<sheetData>") + "<sheetData>".length;
    sdClose = sheetXml.indexOf("</sheetData>", sdOpen);
    sheetDataInner = "";
  } else {
    return sheetXml;
  }

  type RowEntry = { num: number; xml: string };
  const rows: RowEntry[] = [];
  const rowRe = /<row\b[^>]*>[\s\S]*?<\/row>|<row\b[^/]*\/>/g;
  let rm: RegExpExecArray | null;
  while ((rm = rowRe.exec(sheetDataInner)) !== null) {
    const rowXml = rm[0];
    const r = attr(rowXml, "r");
    if (!r) continue;
    const expanded = rowXml.endsWith("/>")
      ? rowXml.replace(/\/>$/, "></row>").replace(/^<row\b/, "<row")
      : rowXml;
    rows.push({ num: Number(r), xml: expanded });
  }

  const stripRow = (p: GenericPatch & { row: number }): GenericPatch => {
    const { row: _r, ...rest } = p;
    return rest as GenericPatch;
  };

  const handled = new Set<number>();
  for (const row of rows) {
    const p = byRow.get(row.num);
    if (!p) continue;
    row.xml = patchRow(row.xml, row.num, p.map(stripRow));
    handled.add(row.num);
  }
  for (const [rowNum, p] of byRow) {
    if (handled.has(rowNum)) continue;
    const empty = `<row r="${rowNum}"></row>`;
    const patched = patchRow(empty, rowNum, p.map(stripRow));
    rows.push({ num: rowNum, xml: patched });
  }

  rows.sort((a, b) => a.num - b.num);
  const newSheetDataInner = rows.map((r) => r.xml).join("");
  return sheetXml.slice(0, sdOpen) + newSheetDataInner + sheetXml.slice(sdClose);
};


/**
 * Patch the EST workbook in place.
 *
 * @param originalBytes Original file contents (.xlsx or .xlsm).
 * @param patches       Numeric cell patches for the realtimedata worksheet.
 * @param textPatches   Optional text patches for arbitrary other worksheets,
 *                      keyed by case-insensitive sheet name (e.g. "PRVFIN").
 */
export const patchEstWorkbook = async (
  originalBytes: ArrayBuffer,
  patches: EstCellPatch[],
  textPatches?: Record<string, EstTextPatch[]>,
): Promise<PatchResult> => {
  const zip = await JSZip.loadAsync(originalBytes);

  const workbookFile = zip.file("xl/workbook.xml");
  const relsFile = zip.file("xl/_rels/workbook.xml.rels");
  if (!workbookFile || !relsFile) {
    throw new Error("Invalid Excel file: missing xl/workbook.xml or its rels.");
  }
  const workbookXml = await workbookFile.async("string");
  const relsXml = await relsFile.async("string");

  const sheets = parseSheets(workbookXml);
  const target = sheets.find((s) => SHEET_ALIASES.includes(normKey(s.name)));
  if (!target) {
    throw new Error(`Workbook has no "realtimedata" tab (found: ${sheets.map((s) => s.name).join(", ")}).`);
  }

  const relMap = parseRels(relsXml);
  const resolveSheetPath = (rId: string): string | null => {
    const relTarget = relMap.get(rId);
    if (!relTarget) return null;
    return relTarget.startsWith("/")
      ? relTarget.slice(1)
      : `xl/${relTarget.replace(/^\.\//, "")}`;
  };

  const sheetPath = resolveSheetPath(target.rId);
  if (!sheetPath) throw new Error(`Workbook rels missing target for ${target.rId}.`);
  const sheetFile = zip.file(sheetPath);
  if (!sheetFile) throw new Error(`Worksheet file not found in package: ${sheetPath}`);

  // 4a. Patch the realtimedata sheet (numeric).
  const numericPatches: Array<GenericPatch & { row: number }> = patches.map((p) => ({
    row: p.row,
    col: p.col,
    kind: "number",
    value: p.value,
  }));
  const sheetXml = await sheetFile.async("string");
  const newSheetXml = patchSheetXml(sheetXml, numericPatches);
  zip.file(sheetPath, newSheetXml);

  // 4b. Patch any additional sheets with text patches (e.g. PRVFIN col C).
  if (textPatches) {
    for (const [sheetNameKey, tps] of Object.entries(textPatches)) {
      if (!tps || tps.length === 0) continue;
      const wantedKey = normKey(sheetNameKey);
      const otherTarget = sheets.find((s) => normKey(s.name) === wantedKey);
      if (!otherTarget) {
        console.warn(`[patchEstWorkbook] Sheet "${sheetNameKey}" not found — skipping text patches.`);
        continue;
      }
      const otherPath = resolveSheetPath(otherTarget.rId);
      if (!otherPath) continue;
      const otherFile = zip.file(otherPath);
      if (!otherFile) continue;
      const otherXml = await otherFile.async("string");
      const generic: Array<GenericPatch & { row: number }> = tps.map((p) => ({
        row: p.row,
        col: p.col,
        kind: "inlineStr",
        value: p.value,
      }));
      const newOtherXml = patchSheetXml(otherXml, generic);
      zip.file(otherPath, newOtherXml);
    }
  }

  // 5. Force Excel to recompute formulas on open by:
  //    (a) deleting calcChain.xml (cache becomes stale once we edit cells)
  //    (b) setting fullCalcOnLoad=1 in workbook.xml's calcPr element.
  if (zip.file("xl/calcChain.xml")) {
    zip.remove("xl/calcChain.xml");
    const ctFile = zip.file("[Content_Types].xml");
    if (ctFile) {
      const ct = await ctFile.async("string");
      const cleaned = ct.replace(
        /<Override\s+PartName="\/xl\/calcChain\.xml"[^>]*?\/>/,
        "",
      );
      if (cleaned !== ct) zip.file("[Content_Types].xml", cleaned);
    }
    if (zip.file("xl/_rels/workbook.xml.rels")) {
      const rels = await zip.file("xl/_rels/workbook.xml.rels")!.async("string");
      const cleanedRels = rels.replace(
        /<Relationship\b[^>]*?Target="calcChain\.xml"[^>]*?\/>/,
        "",
      );
      if (cleanedRels !== rels) zip.file("xl/_rels/workbook.xml.rels", cleanedRels);
    }
  }
  let newWorkbookXml = workbookXml;
  if (/<calcPr\b[^/]*\/>/.test(newWorkbookXml)) {
    newWorkbookXml = newWorkbookXml.replace(/<calcPr\b([^/]*)\/>/, (_full, inner) => {
      const cleaned = inner.replace(/\sfullCalcOnLoad="[^"]*"/, "");
      return `<calcPr${cleaned} fullCalcOnLoad="1"/>`;
    });
  } else if (!/<calcPr\b/.test(newWorkbookXml)) {
    newWorkbookXml = newWorkbookXml.replace(
      /<\/workbook>\s*$/,
      '<calcPr fullCalcOnLoad="1"/></workbook>',
    );
  }
  if (newWorkbookXml !== workbookXml) {
    zip.file("xl/workbook.xml", newWorkbookXml);
  }

  const out = await zip.generateAsync({
    type: "uint8array",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });

  return {
    bytes: out,
    sheetName: target.name,
    sheetPath,
    cellsWritten: patches.length,
  };
};

